import React, { useState, useEffect } from "react";
import { Info } from "lucide-react";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import Banner from "../banner";
import chopLogo from "@/assets/chop_logo.svg";
import useConfigService from "@/services/config.service"; 
import { BANNER_DISMISS_KEY } from "@/constants/app.constant";
import { ConversationType } from "../../constants/chat.constant";
import { NAVIGATION_ITEMS } from "@/constants/navigation.constant";
import { SensitiveChatButton } from "../chat/sensitive-chat-button";
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';

/**
 * Props for the ChatHeader component
 */
interface ChatHeaderProps {
  /** Whether the conversation has messages */
  hasMessages: boolean | null;
  /** Callback to set show upcoming state */
  setShowUpcoming: (value: boolean) => void;
  /** Whether sensitive chat mode is active */
  isSensitive?: boolean;
  /** Callback fired when sensitive mode is toggled */
  onToggleSensitive?: () => void;
  /** Whether sensitive mode toggle is disabled */
  isSensitiveDisabled?: boolean;
  /** Type of conversation */
  conversationType: ConversationType;
}

/**
 * ChatHeader component displays the header for the chat page with banner message,
 * logo, sidebar trigger, sensitive chat button, and info button. Fetches and displays
 * banner message from config service. Handles banner dismissal via session storage.
 * 
 * @component
 * @param {ChatHeaderProps} props - Component props
 * @param {boolean | null} props.hasMessages - Whether conversation has messages
 * @param {(value: boolean) => void} props.setShowUpcoming - Set show upcoming callback
 * @param {boolean} [props.isSensitive=false] - Sensitive mode flag
 * @param {() => void} [props.onToggleSensitive] - Toggle sensitive callback
 * @param {boolean} [props.isSensitiveDisabled=false] - Sensitive disabled flag
 * @returns {JSX.Element} Chat header with banner, logo, and controls
 * 
 * @example
 * ```tsx
 * <ChatHeader
 *   hasMessages={true}
 *   setShowUpcoming={setShow}
 *   isSensitive={false}
 *   onToggleSensitive={handleToggle}
 * />
 * ```
 */
const ChatHeader: React.FC<ChatHeaderProps> = ({
  hasMessages,
  setShowUpcoming,
  isSensitive = false,
  onToggleSensitive,
  isSensitiveDisabled = false,
  conversationType,
}) => {
  const { state: sidebarState, isMobile } = useSidebar();
  const { fetchBannerMessage } = useConfigService(); 

  const activeNavItem = NAVIGATION_ITEMS.find((item) => item.type === conversationType);

  const [showBanner, setShowBanner] = useState(true);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const [loadingBanner, setLoadingBanner] = useState<boolean>(true);

  useEffect(() => {

    const loadBanner = async () => {
      try {
        const response = await fetchBannerMessage();
        const message = response?.data?.message?.trim();

        if (message) {
          setBannerMessage(message);
        } else {
          setShowBanner(false);
        }
      } catch (error) {
        console.error("Error fetching banner message:", error);
        setShowBanner(false);
      } finally {
        setLoadingBanner(false);
      }
    };

    const dismissBanner = sessionStorage.getItem(BANNER_DISMISS_KEY);

    if (dismissBanner) {
      setShowBanner(false);
    } else {
      loadBanner();
    }
  }, [fetchBannerMessage]);

  return (
    <div>
      {/* Conditional Banner */}
      {!loadingBanner && showBanner && bannerMessage && (
        <div className="banner-container">
          <Banner
            dismissible
            onDismiss={() => {
              sessionStorage.setItem(BANNER_DISMISS_KEY, "true");
              setShowBanner(false);
            }}
          >
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} className="underline" target="_blank" rel="noreferrer" />
                )
              }}
            >
              {bannerMessage}
            </ReactMarkdown>
          </Banner>
        </div>
      )}

      {/* Header Section */}
      <div
        className={cn(
          "relative flex items-center text-primary-500 px-6 mobileHeader",
          showBanner ? "pt-3 pb-3" : "md:pt-3 pt-6 pb-6 md:pb-3",
          activeNavItem ? "bg-primary-500/15" : ""
        )}
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          {(sidebarState === "collapsed" || isMobile) && (
            <SidebarTrigger />
          )}
          {activeNavItem && (
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center h-7 w-7 rounded ${activeNavItem.iconColor ?? ''}`}>
                <activeNavItem.icon className="h-5 w-5 text-white" />
              </span>
              <span className="text-base font-medium">{activeNavItem.title}</span>
            </div>
          )}
        </div>

        <div className="flex-1 flex justify-center items-center">
          {hasMessages && isMobile ? (
            <div className="w-[175px]">
              <img
                src={chopLogo}
                alt="Children's Hospital of Philadelphia"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            /* FOR UAT TESTING: added the conversationType check so feedback is recorded for Policy Assistant */
            onToggleSensitive && (!hasMessages || isSensitive) && (conversationType===ConversationType.CHOP_GPT) && (
              <SensitiveChatButton
                isSensitive={isSensitive}
                onToggle={onToggleSensitive}
                disabled={isSensitiveDisabled}
                hideTextOnMobile={false}
              />
            )
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {onToggleSensitive && (!hasMessages || isSensitive) && hasMessages && isMobile && (
            <SensitiveChatButton
              isSensitive={isSensitive}
              onToggle={onToggleSensitive}
              disabled={isSensitiveDisabled}
              hideTextOnMobile={true}
            />
          )}
          <button
            className="rounded-md p-2 hover:bg-gray-100 active:bg-gray-200"
            onClick={() => setShowUpcoming(true)}
            aria-label="Show upcoming changes"
          >
            <Info size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
